import { getInitials } from '../../utils/bookingFormat';
import type { PublicBookingStaff } from '../../types/publicBooking';

/**
 * `undefined` means the customer has not chosen yet, `null` means they picked
 * "Any available" — a distinction the step-1 guard depends on, since "no
 * preference" is a valid answer but "not answered" is not.
 */
export type StylistChoice = number | null | undefined;

interface StylistPickerProps {
	staff: PublicBookingStaff[];
	value: StylistChoice;
	onChange: (value: number | null) => void;
}

// Warm portrait backgrounds, cycled so adjacent cards differ. Only shown for
// stylists without a portfolio photo.
const CARD_GRADIENTS = [
	'linear-gradient(145deg,#8b4513,#c4762a)',
	'linear-gradient(145deg,#5c3317,#9b6845)',
	'linear-gradient(145deg,#6b3a20,#b07040)',
	'linear-gradient(145deg,#7a3e28,#bf7048)',
	'linear-gradient(145deg,#4a2810,#8b5e3c)',
	'linear-gradient(145deg,#3d1f0a,#7a4f28)',
];

/** Human label for a grade code, e.g. "MASTER" → "Master". */
const formatGrade = (grade: string): string =>
	grade.charAt(0).toUpperCase() + grade.slice(1).toLowerCase();

/**
 * Step 1 of the paid booking flow: pick a stylist, or let the salon assign one.
 *
 * The "See Work" button from the design is intentionally absent — there is no
 * portfolio endpoint behind it yet. `portfolioImageUrls` is already read here
 * for the card photo, so it will be the natural hook when that ships.
 */
const StylistPicker = ({ staff, value, onChange }: StylistPickerProps) => (
	<>
		<button
			type="button"
			className={`pb-stylist__any${value === null ? ' is-selected' : ''}`}
			onClick={() => onChange(null)}
		>
			<span className="pb-stylist__any-icon">
				<svg
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					stroke="currentColor"
					strokeWidth="1.8"
					strokeLinecap="round"
					strokeLinejoin="round"
				>
					<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
					<circle cx="9" cy="7" r="4" />
					<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
					<path d="M16 3.13a4 4 0 0 1 0 7.75" />
				</svg>
			</span>
			<span className="pb-stylist__any-info">
				<span className="pb-stylist__any-name">Any Available Stylist</span>
				<span className="pb-stylist__any-desc">
					We&apos;ll assign the best available stylist for your appointment
				</span>
			</span>
		</button>

		<div className="pb-stylist__grid">
			{staff.map((member, index) => {
				const isSelected = value === member.id;
				const photo = member.portfolioImageUrls?.[0];
				return (
					<button
						key={member.id}
						type="button"
						className={`pb-stylist__card${isSelected ? ' is-selected' : ''}`}
						onClick={() => onChange(member.id)}
						aria-pressed={isSelected}
					>
						<span
							className="pb-stylist__photo"
							style={
								photo
									? undefined
									: {
											background:
												CARD_GRADIENTS[index % CARD_GRADIENTS.length],
										}
							}
						>
							{photo ? (
								<img src={photo} alt="" loading="lazy" />
							) : (
								<span className="pb-stylist__initials">
									{getInitials(member.name)}
								</span>
							)}
							{isSelected && <span className="pb-stylist__badge">✓</span>}
						</span>
						<span className="pb-stylist__body">
							<span className="pb-stylist__name">{member.name}</span>
							{(member.grade || member.role) && (
								<span className="pb-stylist__grade">
									{member.grade ? formatGrade(member.grade) : member.role}
								</span>
							)}
							<span className="pb-stylist__book">
								{isSelected ? '✓ Selected' : 'Book'}
							</span>
						</span>
					</button>
				);
			})}
		</div>
	</>
);

export default StylistPicker;
